import { useEffect, useState } from 'react';
import { OlympiadsApi } from 'api';
import { convertOlympiadFromApi } from 'features/olympiads/helpers';
import { Olympiad, OlympiadStatus } from 'features/olympiads/model';
import { useAuthContext } from 'hooks/useAuthContext';
import useUserData from 'hooks/useUserData';
import { isTableStatisticYear, OlympiadEducationYear, toYearLearn } from '../../constants/years';

export const useOlympiadStatisticDetails = (
  olympiadId: string | undefined,
  olympiadName: string | undefined,
  olympiadStatus: OlympiadStatus | undefined,
  selectedYear: OlympiadEducationYear | undefined,
  enabled: boolean,
) => {
  const { loaded: authLoaded } = useAuthContext();
  const userData = useUserData();
  const [data, setData] = useState<Olympiad | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const selectedYearKey = selectedYear?.[0];

  useEffect(() => {
    const isValid =
      enabled && olympiadId && olympiadStatus && selectedYear && isTableStatisticYear(selectedYear);

    if (!isValid) {
      setData(null);
      setLoading(false);
      setError(false);
      return;
    }

    if (!authLoaded) {
      setLoading(true);
      setError(false);
      return;
    }

    let cancelled = false;
    const isRegionUser = !!(userData?.isLargeCity || userData?.isRegion);
    const yearLearn = toYearLearn(selectedYear);

    const fetchDetails = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = isRegionUser
          ? await OlympiadsApi.getOlyimpiadsListForRegion(yearLearn, olympiadStatus)
          : await OlympiadsApi.getOlyimpiadsList(yearLearn, olympiadStatus);

        if (cancelled) return;

        const list = Array.isArray(response?.olympiads) ? response.olympiads : [];
        if (!list.length) {
          setError(true);
          setData(null);
          return;
        }
        const index = list.findIndex(
          (item) =>
            item.olympiad_id === olympiadId ||
            (!!olympiadName && item.name?.trim() === olympiadName.trim()),
        );

        if (index >= 0 && list[index]) {
          setData(convertOlympiadFromApi(list[index], index, userData?.id));
        } else {
          setData(null);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(true);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetails();

    return () => {
      cancelled = true;
    };
  }, [
    authLoaded,
    enabled,
    olympiadId,
    olympiadName,
    olympiadStatus,
    selectedYearKey,
    userData?.id,
    userData?.isLargeCity,
    userData?.isRegion,
  ]);

  return { data, loading, error };
};
